import { faStar } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { BenchmarkFindingsHint } from '@/app/(client)/audits/-components/digitized/BenchmarkFindingsHint';
import { BenchmarkScorePrompt } from '@/app/(client)/audits/-components/digitized/BenchmarkScorePrompt';
import { BenchmarkScoreSummary } from '@/app/(client)/audits/-components/digitized/BenchmarkScoreSummary';
import { BenchmarkScoringCard } from '@/app/(client)/audits/-components/digitized/BenchmarkScoringCard';
import { BenchmarkScoringFailedCard } from '@/app/(client)/audits/-components/digitized/BenchmarkScoringFailedCard';
import { BenchmarkSectionScoresTable } from '@/app/(client)/audits/-components/digitized/BenchmarkSectionScoresTable';
import { CustomScoringCard } from '@/app/(client)/audits/-components/digitized/CustomScoringCard';
import { ManagementSystemsTable } from '@/app/(client)/audits/-components/digitized/ManagementSystemsTable';
import { OverallRating } from '@/app/(client)/audits/-components/digitized/OverallRating';
import { PerformanceAreaTable } from '@/app/(client)/audits/-components/digitized/PerformanceAreaTable';
import { ReconciliationSummary } from '@/app/(client)/audits/-components/digitized/ReconciliationSummary';
import { ReconciliationTable } from '@/app/(client)/audits/-components/digitized/ReconciliationTable';
import {
  type ScoreBreakdownTab,
  ScoreBreakdownTabs,
} from '@/app/(client)/audits/-components/digitized/ScoreBreakdownTabs';
import { useAuditBenchmark } from '@/app/(client)/audits/-hooks/useAuditBenchmark';
import { useAuditDetail } from '@/app/(client)/audits/-hooks/useAuditDetail';
import { useAuditDigitization } from '@/app/(client)/audits/-hooks/useAuditDigitization';
import { useBenchmarkSourceFindings } from '@/app/(client)/audits/-hooks/useBenchmarkSourceFindings';
import { canApplyBenchmarkTemplate } from '@/app/(client)/audits/-utils/benchmarkEligibility';
import {
  buildAreaNames,
  buildReconciliation,
} from '@/app/(client)/audits/-utils/benchmarkReconciliation';
import {
  findManagementSystemAreaRatings,
  findManagementSystems,
  findPerformanceAreaRatings,
  findRatedSections,
  isBsciAuditType,
  isSedexAuditType,
} from '@/app/(client)/audits/-utils/digitized';
import { scoreBreakdownView } from '@/app/(client)/audits/-utils/scoreBreakdown';
import { LinkedFindingDetailsSheet } from '@/app/(client)/capa/-components/LinkedFindingDetailsSheet';
import { useLinkedFindingSheet } from '@/app/(client)/capa/-hooks/useLinkedFindingSheet';
import { EmptyState } from '@/components/ui/empty-state';
import { useFeatures } from '@/hooks/useFeatures';
import { useImpactAreasQuery } from '@/hooks/useImpactAreasQuery';
import { useTranslator } from '@/hooks/useTranslator';
import { SUPPORT_CONTACT_EMAIL } from '@/lib/constants/contact';

export const Route = createFileRoute(
  '/(client)/audits/(pages)/(detail)/$auditId/score/',
)({
  component: AuditScorePage,
});

function AuditScorePage() {
  const { auditId } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'auditDetail' });
  const { isSocialScoreEnabled } = useFeatures();
  const { data: audit } = useAuditDetail(auditId);
  const { data: digitized } = useAuditDigitization(auditId);
  const {
    result: benchmark,
    isScoring,
    hasFailed: scoringFailed,
    open: openBenchmark,
    clear: clearBenchmark,
  } = useAuditBenchmark(auditId);
  // The taxonomy the extraction named each rated area with — the reconciliation
  // resolves those names through it. Same query the findings table runs.
  const { data: impactAreas } = useImpactAreasQuery();
  const areaNames = useMemo(
    () => buildAreaNames(impactAreas ?? []),
    [impactAreas],
  );
  const { findings: sourceFindings, isLoading: isLoadingFindings } =
    useBenchmarkSourceFindings(auditId, audit?.companyId ?? undefined);

  const sections = digitized?.sections ?? [];
  const auditTypeId = audit?.auditTypeId;
  const performanceAreas = findPerformanceAreaRatings(sections);
  const managementSystems = findManagementSystems(sections);
  const ratedSections = findRatedSections(sections);

  // The slot below the rating holds the template's score once it has been
  // applied, and the invitation to apply it before that — on the same terms
  // the banner is offered on, from the same predicate. This tab used to decide
  // for itself and left out the digitization half of the rule, which offered
  // the template on audits that had never been analysed.
  const canApplyBenchmark = canApplyBenchmarkTemplate({
    isFeatureEnabled: isSocialScoreEnabled,
    findingCount: sourceFindings.length,
    isLoadingFindings,
  });
  const isSedex = isSedexAuditType(auditTypeId);
  const hasBsci = isBsciAuditType(auditTypeId) && performanceAreas.length > 0;
  const hasSedex = isSedex && !!managementSystems;
  // The letters the digitization stamps on sections carry the audit tab only
  // for schemes with no dedicated table extracted — and never for SMETA, which
  // has no A–E scale at all: its per-area evaluation is the management-systems
  // recommendation matrix, and a SMETA audit whose matrix extracted as noise
  // shows the empty state until re-analysis produces a real one, rather than
  // letters the scheme never issued.
  const hasRatedSections = !hasBsci && !isSedex && ratedSections.length > 0;
  const hasScoreData = hasBsci || hasSedex || hasRatedSections;

  /** Which breakdown the scoring column is showing.
   *
   *  `null` until the reader picks: the default depends on data. An audit with
   *  extracted scheme scores opens on those — the template is a reading of the
   *  record and the record is what a reader arrives expecting. An audit with
   *  no scheme data yet opens on the template's, since the audit tab holds
   *  only the digitize prompt and landing there would put an empty state in
   *  front of the score the reader just produced. */
  const [breakdownTab, setBreakdownTab] = useState<ScoreBreakdownTab | null>(
    null,
  );
  const activeTab: ScoreBreakdownTab =
    breakdownTab ?? (hasScoreData ? 'audit' : 'template');

  // The findings the applied template did not score — everything in the
  // source list whose id no scored finding claims. Handed to the breakdown so
  // its rows account for the findings tab's one-for-one.
  const scoredSourceIds = new Set(
    (benchmark?.findings ?? []).map(finding => finding.sourceFindingId),
  );
  const unscoredFindings = benchmark
    ? sourceFindings.filter(finding => !scoredSourceIds.has(finding.id))
    : [];

  // The finding sheet, opened IN PLACE from the itemized lines below — the
  // same linked-finding bundle the action and plan detail pages use, so the
  // sheet here is the sheet everywhere.
  const [openFindingId, setOpenFindingId] = useState<string | null>(null);
  const findingSheet = useLinkedFindingSheet(openFindingId);
  const handleOpenFinding = (findingId: string) => {
    setOpenFindingId(findingId);
    findingSheet.setOpen(true);
  };
  // Any scheme with its own per-area grades has two readings to reconcile:
  // BSCI publishes a dedicated Performance Area Rating table; SMETA rates each
  // code area across four management dimensions in its own vocabulary
  // ("Robust management systems" … "Not addressed"), folded to the worst per
  // area — SMETA has no A–E scale, so the letters the digitization stamps on
  // sections are its own synthesis and only the last resort. SLCP has no
  // equivalent yet.
  const managementAreaRatings = findManagementSystemAreaRatings(sections);
  // The rated-sections fallback is off for SMETA for the same reason as the
  // audit tab's: reconciling letters the scheme never issued asserts a scale
  // it does not have. A SMETA audit reconciles its recommendation statuses or
  // nothing.
  const schemeAreaRatings =
    performanceAreas.length > 0
      ? performanceAreas
      : managementAreaRatings.length > 0
        ? managementAreaRatings
        : isSedex
          ? []
          : ratedSections;
  const canReconcile = schemeAreaRatings.length > 0;
  const { showTabs, showAudit, showTemplate, showReconciliation } =
    scoreBreakdownView({
      hasScoreData,
      hasBenchmark: !!benchmark,
      canReconcile,
      tab: activeTab,
    });
  const reconciliation =
    showReconciliation && benchmark
      ? buildReconciliation(
          schemeAreaRatings,
          benchmark.sectionScores,
          impactAreas ?? [],
          benchmark.ratedAreas,
        )
      : null;

  return (
    <div className='grid grid-cols-1 @5xl:grid-cols-5 @5xl:items-start gap-4 @5xl:gap-3 p-4 @md:p-6'>
      {/* Left: both scores, one under the other — the audit's own overall
          rating and, directly beneath it, the template's re-reading of that
          same audit. They share a column because the only useful thing to do
          with either number is hold it against the other, and a reader should
          not have to cross three tables of findings to do it. The order makes
          the same argument the center column does: the scheme's grade is the
          record, the template's score is a reading of it. */}
      <aside className='@5xl:col-start-1 @5xl:col-span-1 @5xl:sticky @5xl:top-16 flex flex-col gap-4'>
        <OverallRating
          rating={digitized?.overall_rating ?? null}
          auditedBy={audit?.auditedBy}
          t={t}
        />
        {/* A queued run, shown where the answer lands. The card is replaced
            rather than overlaid: a previous score sitting under a spinner
            reads as the one being computed, and it is not. */}
        {isScoring ? <BenchmarkScoringCard t={t} /> : null}
        {!isScoring && scoringFailed ? (
          <BenchmarkScoringFailedCard onRetry={openBenchmark} t={t} />
        ) : null}
        {!isScoring && benchmark ? (
          <>
            <BenchmarkScoreSummary
              result={benchmark}
              onReapply={openBenchmark}
              onRemove={clearBenchmark}
              t={t}
            />
            {/* Directly under the score, because "how was that number reached"
                is the question the number provokes and the answer is on
                another tab. */}
            <BenchmarkFindingsHint
              actionRender={
                <Link to='/audits/$auditId/findings' params={{ auditId }} />
              }
              t={t}
            />
          </>
        ) : null}
        {/* The prompt stands in the score's own slot, so applying the template
            fills the card in place instead of moving it to another column. */}
        {!benchmark && canApplyBenchmark ? (
          <BenchmarkScorePrompt onApply={openBenchmark} t={t} />
        ) : null}
      </aside>

      {/* Center: the scoring & evaluation tables — three columns wide, the
          width of three top stat cards. The scheme's own tables come first and
          the template's sit under them: the audit is the source, the template
          is a reading of it, and reversing that order would present the
          reading as the record. Schemes with no native scoring of their own
          (SMETA, SLCP) show only the template's, which is the case the
          template was built for. */}
      <div className='@5xl:col-start-2 @5xl:col-span-3 min-w-0 flex flex-col gap-6'>
        {/* The switch sits above both tables rather than inside either card's
            header: it governs which card is on screen, and a control that
            replaces its own container cannot live in it. */}
        {showTabs ? (
          <ScoreBreakdownTabs
            value={activeTab}
            onChange={setBreakdownTab}
            auditLabel={t('score.breakdown.audit')}
            templateLabel={t('score.breakdown.template')}
            {...(canReconcile
              ? {
                  reconciliationLabel: t('score.breakdown.reconciliation'),
                }
              : {})}
          />
        ) : null}

        {showAudit ? (
          hasScoreData ? (
            <>
              {hasBsci ? (
                <PerformanceAreaTable rows={performanceAreas} t={t} />
              ) : null}
              {hasSedex && managementSystems ? (
                <ManagementSystemsTable data={managementSystems} t={t} />
              ) : null}
              {hasRatedSections ? (
                <PerformanceAreaTable
                  rows={ratedSections}
                  title={t('score.sectionRatings.title')}
                  t={t}
                />
              ) : null}
            </>
          ) : (
            // The Audit-score tab on an un-analyzed audit: the scheme's own
            // numbers don't exist yet, and this is where a reader learns why —
            // rather than the tab silently not being offered.
            <EmptyState
              icon={<FontAwesomeIcon icon={faStar} className='size-5' />}
              title={t('score.empty.title')}
              description={t('score.empty.description')}
            />
          )
        ) : null}

        {showTemplate && benchmark ? (
          <BenchmarkSectionScoresTable
            areaNames={areaNames}
            sections={benchmark.sectionScores}
            findings={benchmark.findings}
            onOpenFinding={handleOpenFinding}
            unscoredFindings={unscoredFindings}
            total={benchmark.score}
            t={t}
          />
        ) : null}

        {reconciliation && benchmark ? (
          <>
            {/* The generated read of the comparison, above the rows it
                reads. Written once by the run, not assembled here, and
                absent entirely when the run has none. */}
            <ReconciliationSummary summary={benchmark.summary} t={t} />
            <ReconciliationTable
              areaNames={areaNames}
              reconciliation={reconciliation}
              findings={benchmark.findings}
              onOpenFinding={handleOpenFinding}
              total={benchmark.score}
              t={t}
            />
          </>
        ) : null}

        {!hasScoreData && !benchmark ? (
          <EmptyState
            icon={<FontAwesomeIcon icon={faStar} className='size-5' />}
            title={t('score.empty.title')}
            description={t('score.empty.description')}
          />
        ) : null}
      </div>

      {/* Right: scoring this tab cannot produce on its own — the scheme's and
          the template's both live in the left column now. */}
      <LinkedFindingDetailsSheet
        findingId={openFindingId}
        sheet={findingSheet}
      />

      <aside className='@5xl:col-start-5 @5xl:col-span-1 @5xl:sticky @5xl:top-16 flex flex-col gap-4'>
        <CustomScoringCard
          onContact={() => {
            window.location.href = `mailto:${SUPPORT_CONTACT_EMAIL}`;
          }}
          t={t}
        />
      </aside>
    </div>
  );
}
